import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import FormattedInput from '../../components/FormatdInput/formatFunctions';
import api from '../../services/api';

const MINOR_ENDPOINT = 'internals/visitor/minor/';

const minorSteps = [
  { id: 1, label: 'Informações Pessoais' },
  { id: 2, label: 'Interno Associado' },
  { id: 3, label: 'Endereço' },
  { id: 4, label: 'Documentos' },
  { id: 5, label: 'Declaração' },
  { id: 6, label: 'Confirmação' },
];

const initialMinorData = {
  name: '',
  responsible: '',
  email: '',
  cpf: '',
  phone_one: '',
  city: '',
  uf: '',
  neighborhood: '',
  public_place: '',
  type_relationship: '',
  Foto_3X4: null,
  Cedula_de_identidade_ou_documento_equivalente: null,
  Cadastro_de_Pessoa_Fisica_CPF: null,
  residencia: null,
  rules: false,
  internal: '',
  intcpf: '',
  intdate_of_birth: '',
  mother: '',
};

const formatDateForInput = (value) => {
  if (!value) return '';
  const stringValue = String(value);
  return stringValue.includes('T') ? stringValue.split('T')[0] : stringValue;
};

function CadastroMenorForm({ listInternals, buildUnitReadParams, relationTypes = [], listState, listCity }) {
  const [formData, setFormData] = useState(initialMinorData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [filteredInternals, setFilteredInternals] = useState([]);
  const [selectedInternal, setSelectedInternal] = useState(null);
  const [states, setStates] = useState([]);
  const [stateSearchQuery, setStateSearchQuery] = useState('');
  const [citySearchQuery, setCitySearchQuery] = useState('');
  const [filteredStates, setFilteredStates] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);
  const [shouldSearchState, setShouldSearchState] = useState(false);
  const [shouldSearchCity, setShouldSearchCity] = useState(false);

  const requiredFileFields = useMemo(
    () => ([
      'Foto_3X4',
      'Cedula_de_identidade_ou_documento_equivalente',
      'Cadastro_de_Pessoa_Fisica_CPF',
      'residencia',
    ]),
    []
  );

  const normalizeText = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();

  useEffect(() => {
    const loadStates = async () => {
      if (typeof listState !== 'function') {
        setStates([]);
        return;
      }

      try {
        const response = await listState();
        setStates(Array.isArray(response?.results) ? response.results : []);
      } catch (error) {
        console.error('Erro ao carregar estados para visitante menor:', error);
        setStates([]);
      }
    };

    loadStates();
  }, [listState]);

  useEffect(() => {
    const query = stateSearchQuery.trim();
    if (!shouldSearchState || query.length < 2) {
      setFilteredStates([]);
      return;
    }

    const normalizedQuery = normalizeText(query);
    const filtered = states.filter((state) => {
      const stateName = normalizeText(state?.name || '');
      const stateUf = normalizeText(state?.uf || '');
      return stateName.includes(normalizedQuery) || stateUf.includes(normalizedQuery);
    });
    setFilteredStates(filtered);
  }, [stateSearchQuery, shouldSearchState, states]);

  useEffect(() => {
    const query = citySearchQuery.trim();
    if (!shouldSearchCity || query.length < 3 || !formData.uf || typeof listCity !== 'function') {
      setFilteredCities([]);
      return;
    }

    const debounce = setTimeout(async () => {
      try {
        const response = await listCity({ name: query });
        let cities = Array.isArray(response?.results) ? response.results : [];

        const normalizedStateName = normalizeText(stateSearchQuery);
        const normalizedUf = normalizeText(formData.uf);

        cities = cities.filter((city) => {
          const cityStateName = normalizeText(city?.state_name || city?.state || '');
          const cityStateUf = normalizeText(city?.state_uf || city?.uf || '');
          const matchesStateByName = normalizedStateName
            ? cityStateName.includes(normalizedStateName) || normalizedStateName.includes(cityStateName)
            : false;
          const matchesStateByUf = normalizedUf ? cityStateUf === normalizedUf : false;
          return matchesStateByName || matchesStateByUf;
        });

        setFilteredCities(cities);
      } catch (error) {
        console.error('Erro ao buscar cidades para visitante menor:', error);
        setFilteredCities([]);
      }
    }, 300);

    return () => clearTimeout(debounce);
  }, [citySearchQuery, shouldSearchCity, formData.uf, stateSearchQuery, listCity]);

  const validateStep = (step) => {
    const validationErrors = {};

    if (step === 1) {
      const requiredFields = ['name', 'responsible', 'email', 'phone_one'];
      requiredFields.forEach((field) => {
        if (!formData[field]) {
          validationErrors[field] = 'Campo obrigatório.';
        }
      });
    }

    if (step === 2) {
      const requiredFields = ['internal', 'type_relationship', 'intcpf', 'intdate_of_birth', 'mother'];
      requiredFields.forEach((field) => {
        if (!formData[field]) {
          validationErrors[field] = 'Campo obrigatório.';
        }
      });
    }

    if (step === 3) {
      const requiredFields = ['city', 'uf', 'neighborhood', 'public_place'];
      requiredFields.forEach((field) => {
        if (!formData[field]) {
          validationErrors[field] = 'Campo obrigatório.';
        }
      });
    }

    if (step === 4) {
      requiredFileFields.forEach((field) => {
        if (!formData[field]) {
          validationErrors[field] = 'Documento obrigatório.';
        }
      });
    }

    if (step === 5) {
      if (!formData.rules) {
        validationErrors.rules = 'Você deve concordar com os termos.';
      }
    }

    return validationErrors;
  };

  const validateAll = () => {
    const mergedErrors = {};
    [1, 2, 3, 4, 5].forEach((step) => {
      Object.assign(mergedErrors, validateStep(step));
    });
    return mergedErrors;
  };

  const nextStep = () => {
    const validationErrors = validateStep(currentStep);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    setCurrentStep((prev) => Math.min(6, prev + 1));
  };

  const prevStep = () => {
    setErrors({});
    setCurrentStep((prev) => Math.max(1, prev - 1));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'file' ? files[0] : value,
    }));
  };

  const handleStateSearch = (query) => {
    setStateSearchQuery(query);
    setShouldSearchState(true);
    setFormData((prev) => ({
      ...prev,
      uf: '',
      city: '',
    }));
    setCitySearchQuery('');
    setFilteredCities([]);
    setShouldSearchCity(false);
  };

  const selectState = (state) => {
    const stateName = state?.name || state?.uf || '';
    const stateUf = state?.uf || stateName;
    setFormData((prev) => ({
      ...prev,
      uf: stateUf,
      city: '',
    }));
    setStateSearchQuery(stateName);
    setFilteredStates([]);
    setShouldSearchState(false);
    setCitySearchQuery('');
    setFilteredCities([]);
  };

  const handleCitySearch = (query) => {
    setCitySearchQuery(query);
    setShouldSearchCity(true);
    setFormData((prev) => ({ ...prev, city: '' }));
  };

  const selectCity = (city) => {
    const cityName = city?.name || '';
    setFormData((prev) => ({ ...prev, city: cityName }));
    setCitySearchQuery(cityName);
    setFilteredCities([]);
    setShouldSearchCity(false);
  };

  const handleInternalSearch = (e) => {
    setFormData((prev) => ({ ...prev, internal: e.target.value }));
    setSelectedInternal(null);
  };

  // Busca com debounce: só consulta a API quando o usuário para de digitar; resposta de um termo
  // antigo que chegar atrasada é ignorada. Com interno já escolhido na lista, não busca.
  useEffect(() => {
    const query = (formData.internal || '').trim();
    if (selectedInternal || query.length < 3) {
      setFilteredInternals([]);
      return undefined;
    }

    let isCurrent = true;
    const timer = setTimeout(async () => {
      try {
        const response = await listInternals(buildUnitReadParams({
          name: query,
          limit: 10,
          offset: 0,
        }));

        const rawInternals =
          (Array.isArray(response?.results) && response.results) ||
          (Array.isArray(response?.data) && response.data) ||
          (Array.isArray(response) && response) ||
          [];

        const normalizedInternals = rawInternals
          .map((internal, index) => {
            const name = internal?.name || internal?.Nome || internal?.label || internal;
            if (!name) return null;
            return {
              id: internal?.id || internal?.document || `internal-${index}-${name}`,
              name,
              cpf: internal?.cpf || '',
              mothers_name: internal?.mothers_name || '',
              date_of_birth: formatDateForInput(internal?.date_of_birth),
            };
          })
          .filter(Boolean);

        if (isCurrent) setFilteredInternals(normalizedInternals);
      } catch (error) {
        console.error('Erro ao buscar internos para visitante menor:', error);
        if (isCurrent) setFilteredInternals([]);
      }
    }, 400);

    return () => {
      isCurrent = false;
      clearTimeout(timer);
    };
  }, [formData.internal, selectedInternal, listInternals, buildUnitReadParams]);

  const handleSelectInternal = (internal) => {
    setFormData((prev) => ({
      ...prev,
      internal: internal.name || '',
      intcpf: internal.cpf || prev.intcpf,
      intdate_of_birth: internal.date_of_birth || prev.intdate_of_birth,
      mother: internal.mothers_name || prev.mother,
    }));
    setSelectedInternal(internal);
    setFilteredInternals([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const validationErrors = validateAll();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      setIsSubmitting(false);
      return;
    }

    const selectedName = selectedInternal?.name?.toLowerCase().trim();
    const inputName = (formData.internal || '').toLowerCase().trim();
    if (!selectedName || selectedName !== inputName) {
      toast.warning('Selecione um interno válido da lista de sugestões.');
      setIsSubmitting(false);
      return;
    }

    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      toast.error('Sessão expirada. Faça login novamente para enviar.');
      setIsSubmitting(false);
      return;
    }

    const payload = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') return;
      if (typeof value === 'boolean') {
        payload.append(key, value ? 'true' : 'false');
        return;
      }
      payload.append(key, value);
    });

    try {
      await api.post(MINOR_ENDPOINT, payload, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'multipart/form-data',
        },
        skip401Redirect: true,
      });

      toast.success('Cadastro de visitante menor enviado com sucesso.');
      setFormData(initialMinorData);
      setErrors({});
      setFilteredInternals([]);
      setSelectedInternal(null);
      setStateSearchQuery('');
      setCitySearchQuery('');
      setFilteredStates([]);
      setFilteredCities([]);
      setShouldSearchState(false);
      setShouldSearchCity(false);
      setCurrentStep(1);
    } catch (error) {
      console.error('Erro ao enviar visitante menor:', error);
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.detail ||
        null;
      if (error?.response?.status === 401 && !apiMessage) {
        toast.error('Sessão expirada. Faça login novamente para concluir o cadastro.');
        localStorage.removeItem('accessToken');
        setTimeout(() => {
          window.location.href = '/login';
        }, 1500);
      } else {
        toast.error(apiMessage || 'Erro ao cadastrar visitante menor. Verifique os dados.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="visitante-steps">
        {minorSteps.map((step) => (
          <div
            key={step.id}
            className={`visitante-steps-item ${currentStep === step.id ? 'visitante-active' : ''} ${currentStep > step.id ? 'visitante-complete' : ''}`}
          >
            <span className="visitante-steps-index">
              {step.id.toString().padStart(2, '0')}
            </span>
            <span className="visitante-steps-label">{step.label}</span>
          </div>
        ))}
      </div>

      <form className="visitante-form visitante-form-modern" onSubmit={handleSubmit}>
        {currentStep === 1 && (
          <div className="visitante-step">
            <h2>Informações Pessoais</h2>
            <div className="form-group-duplo">
              <div className="form-group">
                <label>Nome do menor</label>
                <FormattedInput type="nome" name="name" value={formData.name} onChange={handleInputChange} />
                {errors.name && <span className="visitante-error">{errors.name}</span>}
              </div>
              <div className="form-group">
                <label>Responsável</label>
                <FormattedInput type="nome" name="responsible" value={formData.responsible} onChange={handleInputChange} />
                {errors.responsible && <span className="visitante-error">{errors.responsible}</span>}
              </div>
            </div>

            <div className="form-group-duplo">
              <div className="form-group">
                <label>E-mail</label>
                <input type="email" name="email" value={formData.email} onChange={handleInputChange} />
                {errors.email && <span className="visitante-error">{errors.email}</span>}
              </div>
              <div className="form-group">
                <label>Telefone</label>
                <FormattedInput type="telefone" name="phone_one" value={formData.phone_one} onChange={handleInputChange} />
                {errors.phone_one && <span className="visitante-error">{errors.phone_one}</span>}
              </div>
            </div>

            <div className="form-group">
              <label>CPF</label>
              <FormattedInput type="cpf" name="cpf" value={formData.cpf} onChange={handleInputChange} />
              {errors.cpf && <span className="visitante-error">{errors.cpf}</span>}
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <div className="visitante-step">
            <h2>Interno Associado</h2>
            <div className="form-group internal-result">
              <label>Nome do interno associado</label>
              <FormattedInput
                type="nome"
                name="internal"
                value={formData.internal}
                onChange={handleInternalSearch}
                autoComplete="off"
              />
              {errors.internal && <span className="visitante-error">{errors.internal}</span>}

              {filteredInternals.length > 0 && (
                <div className="internal-suggestions">
                  {filteredInternals.map((internal, index) => (
                    <div
                      key={internal.id || internal.name || `minor-internal-option-${index}`}
                      onClick={() => handleSelectInternal(internal)}
                    >
                      {internal.name}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="form-group">
              <label>Grau de parentesco</label>
              <select name="type_relationship" value={formData.type_relationship} onChange={handleInputChange}>
                <option value="">Selecione</option>
                {relationTypes.map((relation) => (
                  <option key={relation.id} value={relation.name}>{relation.name}</option>
                ))}
              </select>
              {errors.type_relationship && <span className="visitante-error">{errors.type_relationship}</span>}
            </div>

            <div className="form-group-duplo">
              <div className="form-group">
                <label>CPF do Interno</label>
                <FormattedInput type="cpf" name="intcpf" value={formData.intcpf} onChange={handleInputChange} />
                {errors.intcpf && <span className="visitante-error">{errors.intcpf}</span>}
              </div>
              <div className="form-group">
                <label>Data de nascimento do interno</label>
                <input type="date" name="intdate_of_birth" value={formData.intdate_of_birth} onChange={handleInputChange} />
                {errors.intdate_of_birth && <span className="visitante-error">{errors.intdate_of_birth}</span>}
              </div>
            </div>

            <div className="form-group">
              <label>Nome da mãe do interno</label>
              <FormattedInput type="nome" name="mother" value={formData.mother} onChange={handleInputChange} />
              {errors.mother && <span className="visitante-error">{errors.mother}</span>}
            </div>
          </div>
        )}

        {currentStep === 3 && (
          <div className="visitante-step">
            <h2>Endereço</h2>
            <div className="form-group-duplo">
              <div className="form-group internal-result">
                <label>Estado</label>
                <input
                  type="text"
                  value={stateSearchQuery}
                  onChange={(e) => handleStateSearch(e.target.value)}
                  placeholder="Digite o estado"
                />
                {filteredStates.length > 0 && (
                  <div className="internal-suggestions">
                    {filteredStates.map((state) => (
                      <div key={state.id || state.uf || state.name} onClick={() => selectState(state)}>
                        {state.name} ({state.uf})
                      </div>
                    ))}
                  </div>
                )}
                {errors.uf && <span className="visitante-error">{errors.uf}</span>}
              </div>
              <div className="form-group internal-result">
                <label>Cidade</label>
                <input
                  type="text"
                  value={citySearchQuery}
                  onChange={(e) => handleCitySearch(e.target.value)}
                  placeholder={formData.uf ? 'Digite a cidade' : 'Selecione um estado primeiro'}
                  disabled={!formData.uf}
                />
                {filteredCities.length > 0 && (
                  <div className="internal-suggestions">
                    {filteredCities.map((city) => (
                      <div key={city.id || city.name} onClick={() => selectCity(city)}>
                        {city.name}
                      </div>
                    ))}
                  </div>
                )}
                {errors.city && <span className="visitante-error">{errors.city}</span>}
              </div>
            </div>

            <div className="form-group-duplo">
              <div className="form-group">
                <label>Bairro</label>
                <FormattedInput type="nome" name="neighborhood" value={formData.neighborhood} onChange={handleInputChange} />
                {errors.neighborhood && <span className="visitante-error">{errors.neighborhood}</span>}
              </div>
              <div className="form-group">
                <label>Logradouro</label>
                <FormattedInput type="nome" name="public_place" value={formData.public_place} onChange={handleInputChange} />
                {errors.public_place && <span className="visitante-error">{errors.public_place}</span>}
              </div>
            </div>
          </div>
        )}

        {currentStep === 4 && (
          <div className="visitante-step">
            <h2>Documentos</h2>
            <div className="documents-section">
              <div>
                <div className="form-group">
                  <label>Foto legivel</label>
                  <input type="file" name="Foto_3X4" onChange={handleInputChange} />
                  {errors.Foto_3X4 && <span className="visitante-error">{errors.Foto_3X4}</span>}
                </div>
                <div className="form-group">
                  <label>Cédula de identidade ou documento equivalente</label>
                  <input type="file" name="Cedula_de_identidade_ou_documento_equivalente" onChange={handleInputChange} />
                  {errors.Cedula_de_identidade_ou_documento_equivalente && (
                    <span className="visitante-error">{errors.Cedula_de_identidade_ou_documento_equivalente}</span>
                  )}
                </div>
                <div className="form-group">
                  <label>Cadastro de Pessoa Física (CPF)</label>
                  <input type="file" name="Cadastro_de_Pessoa_Fisica_CPF" onChange={handleInputChange} />
                  {errors.Cadastro_de_Pessoa_Fisica_CPF && (
                    <span className="visitante-error">{errors.Cadastro_de_Pessoa_Fisica_CPF}</span>
                  )}
                </div>
              </div>
              <div>
                <div className="form-group">
                  <label>Comprovante de residência</label>
                  <input type="file" name="residencia" onChange={handleInputChange} />
                  {errors.residencia && <span className="visitante-error">{errors.residencia}</span>}
                </div>
              </div>
            </div>
          </div>
        )}

        {currentStep === 5 && (
          <div className="visitante-step">
            <h2>Declaração</h2>
            <p>
              <strong>DECLARO</strong>, para os devidos fins de direito, sob as penas da lei, que as informações prestadas
              neste cadastro de visitante menor são verdadeiras e autênticas.
            </p>
            <br />
            <label>
              <input type="checkbox" name="rules" checked={formData.rules} onChange={handleInputChange} />
              Eu concordo com os termos.
            </label>
            {errors.rules && <span className="visitante-error">{errors.rules}</span>}
          </div>
        )}

        {currentStep === 6 && (
          <div className="visitante-step">
            <h2>Confirmação</h2>
            <table className="visitante-summary-table">
              <tbody>
                <tr>
                  <td><strong>Nome do menor:</strong></td>
                  <td>{formData.name}</td>
                </tr>
                <tr>
                  <td><strong>Responsável:</strong></td>
                  <td>{formData.responsible}</td>
                </tr>
                <tr>
                  <td><strong>Email:</strong></td>
                  <td>{formData.email}</td>
                </tr>
                <tr>
                  <td><strong>CPF:</strong></td>
                  <td>{formData.cpf}</td>
                </tr>
                <tr>
                  <td><strong>Telefone:</strong></td>
                  <td>{formData.phone_one}</td>
                </tr>
                <tr>
                  <td><strong>Cidade:</strong></td>
                  <td>{formData.city}</td>
                </tr>
                <tr>
                  <td><strong>Estado:</strong></td>
                  <td>{stateSearchQuery || formData.uf}</td>
                </tr>
                <tr>
                  <td><strong>Bairro:</strong></td>
                  <td>{formData.neighborhood}</td>
                </tr>
                <tr>
                  <td><strong>Logradouro:</strong></td>
                  <td>{formData.public_place}</td>
                </tr>
                <tr>
                  <td><strong>Interno:</strong></td>
                  <td>{formData.internal}</td>
                </tr>
                <tr>
                  <td><strong>Parentesco:</strong></td>
                  <td>{formData.type_relationship}</td>
                </tr>
                <tr>
                  <td><strong>Foto 3X4:</strong></td>
                  <td>{formData.Foto_3X4 ? formData.Foto_3X4.name : 'Não enviado'}</td>
                </tr>
                <tr>
                  <td><strong>Cédula de identidade:</strong></td>
                  <td>{formData.Cedula_de_identidade_ou_documento_equivalente ? formData.Cedula_de_identidade_ou_documento_equivalente.name : 'Não enviado'}</td>
                </tr>
                <tr>
                  <td><strong>Cadastro de Pessoa Física (CPF):</strong></td>
                  <td>{formData.Cadastro_de_Pessoa_Fisica_CPF ? formData.Cadastro_de_Pessoa_Fisica_CPF.name : 'Não enviado'}</td>
                </tr>
                <tr>
                  <td><strong>Comprovante de residência:</strong></td>
                  <td>{formData.residencia ? formData.residencia.name : 'Não enviado'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        <div className="visitante-buttons">
          {currentStep === 1 ? (
            <button type="button" className="visitante-btn-secondary" disabled onClick={prevStep}>
              Voltar
            </button>
          ) : (
            <button type="button" className="visitante-btn-secondary" onClick={prevStep}>
              Voltar
            </button>
          )}

          {currentStep < 6 && (
            <button type="button" className="visitante-btn-primary" onClick={nextStep}>
              Próximo
            </button>
          )}

          {currentStep === 6 && (
            <button
              type="submit"
              className={`visitante-btn-primary ${isSubmitting ? 'btn-disabled' : ''}`}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Enviando...' : 'Enviar'}
            </button>
          )}
        </div>
      </form>
    </>
  );
}

export default CadastroMenorForm;
